// File: packages/i18n/src/LanguageSwitcher.tsx
// Version: 2026.4.7 | ForgeTrack language switcher
// CRITICAL: import from ./types + ./LocaleProvider, NEVER from ./index
//
// CHANGE LOG (2026.4.7):
//   - Added `openRight` prop: when true, popover opens to the right of the
//     trigger instead of stacking above/below it. Required for sidebar use
//     where the trigger is at the left edge of the screen and a stacked
//     popover gets clipped to the narrow sidebar width.
//   - Bumped trigger font 12 → 14, list item font 13 → 15.
//   - Bumped inactive text from rgba(255,255,255,0.55) and group label from
//     0.35 to higher-contrast values that pass WCAG AA on the dark surface.
//   - Trigger now spans full width when used inline (no inline-block).

import { useState, useRef, useEffect } from 'react';
import { useLocale } from './LocaleProvider';
import {
  type Locale,
  LOCALE_DISPLAY_NAMES,
  LOCALE_SHORT_LABELS,
  LOCALE_GROUPS,
} from './types';

interface LanguageSwitcherProps {
  variant?: 'full' | 'compact';
  compact?: boolean;
  className?: string;
  /** true (default) = popover opens upward (sidebar), false = downward (TopBar) */
  openUp?: boolean;
  /** When true, popover opens to the RIGHT of the trigger instead of stacking. */
  openRight?: boolean;
}

const GROUP_LABELS: Record<string, string> = {
  latin: 'Latin',
  cyrillic: 'Cyrillic',
  cjk: 'CJK',
  indic: 'Indic',
  rtl: 'RTL',
};

export function LanguageSwitcher({
  variant,
  compact = false,
  className = '',
  openUp = true,
  openRight = false,
}: LanguageSwitcherProps) {
  const { locale, setLocale } = useLocale();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const isCompact = compact || variant === 'compact';

  // Close on outside click + Escape
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setIsOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, []);

  function handleSelect(loc: Locale) {
    setLocale(loc);
    setIsOpen(false);
  }

  // ── Popover positioning ────────────────────────────────────────────────────
  // openRight: anchor to the LEFT edge of the trigger's right side, gap 10px.
  //            Anchor by `bottom: 0` of trigger so the tall list grows upward
  //            from the bottom of a sidebar. This is what the sidebar wants.
  // !openRight: legacy behavior — stack above (openUp) or below the trigger.
  const popoverStyle: React.CSSProperties = openRight
    ? {
        position: 'absolute',
        left:     'calc(100% + 10px)',
        bottom:   0,
        width:    300,
      }
    : {
        position: 'absolute',
        [openUp ? 'bottom' : 'top']: '100%',
        right:    0,
        marginTop:    openUp ? undefined : 4,
        marginBottom: openUp ? 4 : undefined,
        minWidth: 200,
      };

  return (
    <div
      ref={ref}
      className={className}
      style={{
        position: 'relative',
        display:  openRight ? 'block' : 'inline-block',
        width:    openRight ? '100%' : undefined,
      }}
    >
      <button
        onClick={() => setIsOpen(prev => !prev)}
        style={{
          background:   isOpen ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.06)',
          border:       `1px solid ${isOpen ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.18)'}`,
          borderRadius: 8,
          padding:      '10px 14px',
          color:        '#e8ecf2',
          fontSize:     14,
          fontWeight:   500,
          cursor:       'pointer',
          fontFamily:   "'Outfit', system-ui, sans-serif",
          display:      'flex',
          alignItems:   'center',
          gap:          8,
          width:        openRight ? '100%' : undefined,
          textAlign:    'left',
          transition:   'background 0.15s, border-color 0.15s',
        }}
        aria-label="Select language"
        aria-expanded={isOpen}
      >
        <span style={{ fontSize: 15 }}>🌐</span>
        <span style={{ flex: 1 }}>
          {isCompact ? LOCALE_SHORT_LABELS[locale] : LOCALE_DISPLAY_NAMES[locale]}
        </span>
        <span style={{ fontSize: 9, opacity: 0.7 }}>{openRight ? '▶' : '▼'}</span>
      </button>

      {isOpen && (
        <div
          style={{
            ...popoverStyle,
            background:    'rgba(8,16,32,0.97)',
            border:        '1px solid rgba(255,255,255,0.20)',
            borderRadius:  10,
            padding:       8,
            zIndex:        999,
            maxHeight:     460,
            overflowY:     'auto',
            boxShadow:     '0 16px 48px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04)',
            backdropFilter: 'blur(12px) saturate(1.4)',
          }}
        >
          {Object.entries(LOCALE_GROUPS).map(([group, locales]) => (
            <div key={group}>
              <div style={{
                fontSize: 10,
                fontWeight: 700,
                color: 'rgba(255,255,255,0.62)',
                letterSpacing: '0.10em',
                textTransform: 'uppercase',
                padding: '8px 10px 4px',
                fontFamily: "'Outfit', system-ui, sans-serif",
              }}>
                {GROUP_LABELS[group]}
              </div>
              {locales.map((loc) => (
                <button
                  key={loc}
                  onClick={() => handleSelect(loc)}
                  style={{
                    display:    'block',
                    width:      '100%',
                    textAlign:  'left',
                    background: loc === locale ? 'rgba(52,211,153,0.18)' : 'transparent',
                    border:     'none',
                    borderRadius: 6,
                    padding:    '9px 12px',
                    color:      loc === locale ? '#34d399' : '#e8ecf2',
                    fontSize:   15,
                    fontWeight: loc === locale ? 600 : 400,
                    cursor:     'pointer',
                    fontFamily: "'Outfit', system-ui, sans-serif",
                    transition: 'background 0.12s',
                  }}
                  onMouseEnter={(e) => {
                    if (loc !== locale) e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                  }}
                  onMouseLeave={(e) => {
                    if (loc !== locale) e.currentTarget.style.background = 'transparent';
                  }}
                >
                  {LOCALE_DISPLAY_NAMES[loc]}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
